import Image from 'next/image'
import {FC} from 'react'

import {ContentLayoutWithSidebar} from '@/components/common/content/ContentLayoutWithSidebar'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {getTrademarkDisputesContent} from '@/content/trademarkDisputes'
import {Link, useTranslation} from '@/i18n'
import {
  CONTENT_LAYOUT_WIDTH,
  TRADEMARK_DISPUTE_CASE_COVER_HEIGHT_DESKTOP,
  TRADEMARK_DISPUTE_CASE_COVER_HEIGHT_MOBILE,
} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Locale} from '@/utils/constants'
import {formatDate} from '@/utils/formatDate'
import {routeTo} from '@/utils/router'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {TrademarkDisputeCaseDetail} from '@/utils/trademark-disputes/types'

import {IpCaseDetailRow} from './IpCaseDetailRow'

type IpCaseDetailProps = {
  ipCase: TrademarkDisputeCaseDetail
}

export const IpCaseDetail: FC<IpCaseDetailProps> = ({ipCase}) => {
  const {t} = useTranslation()
  const {
    caseDetailsTitle,
    plaintiffTitle,
    defendantTitle,
    caseNumberTitle,
    jurisdictionTitle,
    industryTitle,
    decisionDateTitle,
    subjectMatterTitle,
    decisionTitle,
  } = getTrademarkDisputesContent()

  const {
    plaintiff,
    defendant,
    caseNumber,
    decision,
    dateOfDecision,
    jurisdiction,
    industry,
    subjects,
    coverImage,
    relatedService,
  } = ipCase

  return (
    <>
      <ContentLayoutWithSidebar relatedServices={[relatedService]}>
        <div className="cover">
          <Image
            src={coverImage}
            alt={plaintiff.name}
            fill
            priority
            sizes={`${USER_TABLET_SCREENSIZE} 100vw, ${CONTENT_LAYOUT_WIDTH}`}
            style={{objectFit: 'cover'}}
          />
        </div>
        <div className="section">
          <div className="title">
            <Text variant={TextVariant.h2}>{t(caseDetailsTitle)}</Text>
          </div>
          <IpCaseDetailRow
            title={t(plaintiffTitle)}
            description={plaintiff.name}
            linkHref={routeTo.trademarkDisputes.judicialParty(plaintiff.slug)}
          />
          <IpCaseDetailRow
            title={t(defendantTitle)}
            description={defendant.name}
            linkHref={routeTo.trademarkDisputes.judicialParty(defendant.slug)}
          />
          <IpCaseDetailRow title={t(caseNumberTitle)} description={caseNumber} />
          <IpCaseDetailRow
            title={t(jurisdictionTitle)}
            description={jurisdiction.name}
            linkHref={routeTo.trademarkDisputes.jurisdiction(jurisdiction.slug)}
          />
          <IpCaseDetailRow
            title={t(industryTitle)}
            description={industry.name}
            linkHref={routeTo.trademarkDisputes.industry(industry.slug)}
          />
          <IpCaseDetailRow
            title={t(decisionDateTitle)}
            description={formatDate(dateOfDecision, Locale.en, false, 'dd MMM, yyyy')}
          />
          <IpCaseDetailRow title={t(subjectMatterTitle)}>
            {subjects.map(({name, slug}) => (
              <span key={slug} className="subject">
                <Text element="span" fontWeight={TextFontWeight.regular} className="underline">
                  <Link href={routeTo.trademarkDisputes.subjectMatter(slug)} prefetch={false}>
                    {name}
                  </Link>
                </Text>
              </span>
            ))}
          </IpCaseDetailRow>
        </div>
        <div className="section">
          <div className="title">
            <Text variant={TextVariant.h2}>{t(decisionTitle)}</Text>
          </div>
          <Text>{decision}</Text>
        </div>
      </ContentLayoutWithSidebar>

      <style jsx>{`
        .cover {
          position: relative;
          width: 100%;
          height: ${TRADEMARK_DISPUTE_CASE_COVER_HEIGHT_DESKTOP};
          margin-bottom: ${theme.spacing[6]};
        }
        .section + .section {
          margin-top: ${theme.spacing[6]};
        }
        .title {
          margin-bottom: ${theme.spacing[5]};
        }
        .subject + .subject::before {
          content: ', ';
        }
        @media ${MOBILE_SCREENSIZE} {
          .cover {
            height: ${TRADEMARK_DISPUTE_CASE_COVER_HEIGHT_MOBILE};
          }
        }
      `}</style>
    </>
  )
}
